import { useRoutes } from "react-router-dom";
import router from "src/router";
import { CssBaseline, Box, Typography } from "@mui/material";
import ThemeProvider from "./theme/ThemeProvider";
import "./content/pages/homepage.module.css";
import { useAuth0 } from "@auth0/auth0-react";
import { useEffect, useState, useRef } from "react";
import { App as CapApp } from "@capacitor/app";
import { Browser } from "@capacitor/browser";
import { callbackUri } from "./auth.config";
import SuspenseLoader from "./components/SuspenseLoader/SuspenseLoader";
import { StatusBar } from "@capacitor/status-bar";
import { isIOS, isAndroid } from "./utils/platformUtils";
import { useNavigate } from "react-router-dom";
import SimpleSplashScreen from "./components/SplashScreen/SimpleSplashScreen";
import { useExternalLinks } from "./hooks/useExternalLinks";
import LoginDialog from "./components/LoginForm/LoginDialog";
import UpdateDialog from "src/components/UpdateDialog/UpdateDialog";
import { getAppInfo, isUpdateRequired, redirectToStore } from "src/utils/getAppVersion";
import { getMinVersions, MinVersionsFetchError } from "src/api/getMinVersions";
import { isPlatform } from "@ionic/react";
import { trackAppLogin } from "./utils/analyticsCapFirebase";
import { trackAppLoginError } from "./utils/analyticsCapFirebase";
import { consumeAnalyticsSignupIntent, getAnalyticsAnonAuthId, getAnalyticsHadUsedFreeCredit, setAnalyticsUser, trackSignUp } from "./utils/analyticsCapFirebase";
import { initializeAdMob } from "./services/adService";
import { Capacitor } from "@capacitor/core";
import { DEV_FRONTEND_URIS, isWebLoginGatePublicPath } from "./config/frontendUrls";

// Dev urls for new authentication (canonical dev.rotogpt.com + legacy chat.dev during migration)
const devUris = [
  ...DEV_FRONTEND_URIS,
];

//check if the app is running in a capacitor browser or hybrid platform
const isRunningInCapacitor = Capacitor.isNativePlatform() || isPlatform("hybrid");
//check if the current path is a public path for the web login gate
const isWebLoginGatePublicPathValue = isWebLoginGatePublicPath(window.location.pathname);
//check if the app is running in dev-prod environment and not in a capacitor browser or hybrid platform and not a public path for the web login gate
const isDev = devUris.includes(process.env.REACT_APP_FRONTEND_URI_WEB) && !isRunningInCapacitor && !isWebLoginGatePublicPathValue;

// Yahoo OAuth web return remounts on /yahoo_callback. Skip Auth0 splash so AuthCallback
// can mount immediately (guest has no splash; Auth0 splash was blocking the callback).
const isYahooCallbackPath = (): boolean => {
  const path = window.location.pathname;
  return path === "/yahoo_callback" || path.startsWith("/yahoo_callback/");
};

function App() {
  const content = useRoutes(router);
  const { handleRedirectCallback, isAuthenticated, isLoading, user } = useAuth0();
  const navigate = useNavigate();
  const onYahooCallback = isYahooCallbackPath();
  //state for show splash screen — skip when returning from Yahoo OAuth
  const [showSplash, setShowSplash] = useState(() => !isYahooCallbackPath());
  //state for video ended — true when skipping splash so route content can render
  const [videoEnded, setVideoEnded] = useState(() => isYahooCallbackPath());
  //state for dev authentication in frontend
  const [isDevAuthenticated, setIsDevAuthenticated] = useState(!isDev);
  const [openLoginDialog, setOpenLoginDialog] = useState(false);
  //state for show update dialog for app version
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  //state for startup error
  const [startupError, setStartupError] = useState<string | null>(null);

  // Check if user is authenticated
  const checkAuth = () => {
    const authStatus = sessionStorage.getItem("isAuthenticated");
    if (!authStatus) {
      setOpenLoginDialog(true);
    } else {
      const storedAuth = sessionStorage.getItem("isAuthenticated");
      if (storedAuth === "true") {
        setIsDevAuthenticated(true);
        setOpenLoginDialog(false);
      } else {
        sessionStorage.removeItem("isAuthenticated");
        setOpenLoginDialog(true);
      }
    }
  };

  const handleLoginSubmit = (username, password) => {
    const isValid = username === process.env.REACT_APP_DEV_USER && password === process.env.REACT_APP_DEV_PASSWORD;

    if (isValid) {
      setIsDevAuthenticated(true);
      sessionStorage.setItem("isAuthenticated", "true");
      setOpenLoginDialog(false);
    }

    return isValid;
  };

  useEffect(() => {
    if (isDev) {
      checkAuth();
    }
  }, [isDev]);

  //use the hook to open external links in the app whit capacitor browser if is mobile
  useExternalLinks();

  // Detect if page loaded with Stripe payment params in Capacitor Browser (fallback detection)
  // This is a fallback in case the inline script in index.html didn't catch it
  useEffect(() => {
    // Check if we're on /stripe-return path
    if (window.location.pathname === "/stripe-return" || window.location.pathname === "/stripe-return/") {
      const urlParams = new URLSearchParams(window.location.search);
      const paymentStatus = urlParams.get("payment");
      const productId = urlParams.get("productId");

      // Check if we're in Capacitor Browser
      const hasCapacitor = !!(window as any).Capacitor;
      const isInCapacitorBrowser = hasCapacitor && (isIOS || isAndroid);

      if (isInCapacitorBrowser && (paymentStatus === "success" || paymentStatus === "cancelled")) {
        console.log("💳 App.tsx: Payment redirect detected in Capacitor Browser (fallback)");
        console.log("Attempting to redirect to native scheme...");

        // Try to redirect to native scheme first (preferred method)
        const appId = "com.rotogpt.chat";
        const nativeCallbackUrl = `${appId}://stripe-callback?payment=${paymentStatus}&productId=${productId || ""}`;

        console.log("🔄 App.tsx: Redirecting to native scheme:", nativeCallbackUrl);

        // Redirect to native scheme - this should close browser and trigger appUrlOpen
        setTimeout(() => {
          try {
            window.location.replace(nativeCallbackUrl);
          } catch (error) {
            console.error("Error redirecting to native scheme, trying fallback:", error);
            // Fallback: try to close browser directly and dispatch event
            Browser.close()
              .then(() => {
                console.log("✅ Browser closed successfully");
                // Dispatch event after browser closes
                setTimeout(() => {
                  const paymentEvent = new CustomEvent("stripePaymentComplete", {
                    detail: {
                      success: paymentStatus === "success",
                      productId: productId,
                    },
                  });
                  window.dispatchEvent(paymentEvent);
                  console.log("🔔 Dispatched stripePaymentComplete event (fallback)");
                }, 200);
              })
              .catch((e) => {
                console.log("Browser close error (expected if not in browser):", e);
              });
          }
        }, 100);
      }
    }
  }, []); // Run once on mount

  // Ref to prevent duplicate callback processing
  const isProcessingCallbackRef = useRef(false);
  const lastProcessedCallbackRef = useRef<string | null>(null);

  useEffect(() => {
    const listener = CapApp.addListener("appUrlOpen", async ({ url }) => {
      console.log("🔄 Received URL:", url);
      if (url.startsWith(callbackUri)) {
        // Check if this is a login callback (has state and code) or error callback
        const isLoginCallback = url.includes("state") && (url.includes("code") || url.includes("error"));

        if (isLoginCallback) {
          // CRITICAL: Prevent duplicate processing of the same callback
          // This can happen because Capacitor may fire appUrlOpen multiple times
          if (isProcessingCallbackRef.current) {
            console.log("⚠️ Callback already being processed, ignoring duplicate");
            return;
          }

          if (lastProcessedCallbackRef.current === url) {
            console.log("⚠️ This callback was already processed, ignoring");
            return;
          }

          isProcessingCallbackRef.current = true;
          lastProcessedCallbackRef.current = url;

          console.log("🔐 Login callback detected in Capacitor");
          console.log("🔍 Full callback URL:", url);

          // Parse URL to check for error or code
          const urlObj = new URL(url);
          const hasError = urlObj.searchParams.has("error");
          const hasCode = urlObj.searchParams.has("code");

          console.log("🔍 URL params:", {
            hasError,
            hasCode,
            error: urlObj.searchParams.get("error"),
            code: hasCode ? "present" : "missing"
          });

          if (hasError) {
            // LOGIN FAILED - Auth0 returned an error
            const err = urlObj.searchParams.get("error") || "auth_error";
            const errDesc = urlObj.searchParams.get("error_description") || "";
            console.error("❌ Login failed with error:", err, errDesc);

            const authId = user?.sub || "unknown";
            await trackAppLogin(authId, "unknown", false);
            await trackAppLoginError(authId, "unknown", err, errDesc);
            await Browser.close();
            console.log("🔄 Hard reloading to /get-started due to login error");
            window.location.replace("/get-started");
            return;
          }

          if (hasCode) {
            // LOGIN SUCCESS - Auth0 returned a code
            console.log("✅ Login successful, code received");
            try {
              console.log("🔄 Calling handleRedirectCallback...");
              await handleRedirectCallback(url);
              console.log("✅ handleRedirectCallback completed successfully");
              console.log("🔄 Auth0 state persisted to storage");

              // Close browser
              try {
                await Browser.close();
                console.log("✅ Browser closed");
              } catch (browserError) {
                console.log("⚠️ Browser close error (may already be closed):", browserError);
              }

              // Use hard reload to avoid race condition
              // This ensures HomePage mounts with correct Auth0 state from scratch
              console.log("🔄 Hard reloading to /home...");
              window.location.replace("/home");
            } catch (error) {
              console.error("❌ handleRedirectCallback failed:", error);
              console.error("❌ Error details:", (error as any)?.message, (error as any)?.error);
              const authId = user?.sub || "unknown";
              await trackAppLogin(authId, "unknown", false);
              await trackAppLoginError(authId, "unknown", "callback_error", (error as any)?.message || "");

              try {
                await Browser.close();
              } catch (browserError) {
                console.log("⚠️ Browser close error:", browserError);
              }

              console.log("🔄 Hard reloading to /get-started due to callback error");
              isProcessingCallbackRef.current = false;
              window.location.replace("/get-started");
            }
            return;
          }

          // Unexpected state: no error and no code
          console.error("❌ Unexpected callback state: no error and no code");
          await Browser.close();
          isProcessingCallbackRef.current = false;
          window.location.replace("/get-started");
        } else {
          // This is a logout callback (no state, no code)
          // After logout, redirect to /get-started (same as web)
          console.log("🔄 Logout callback detected, redirecting to /get-started");
          await Browser.close();
          window.location.replace("/get-started");
        }
      } else if (url.includes("yahoo_callback")) {
        console.log("🔄 Received URL:", url);

        const params = url.split("yahoo_callback")[1];
        console.log("🔄 params:", params);
        // Navigate to AuthCallback, let it handle the logic
        navigate("/yahoo_callback" + params, { replace: true });
        await new Promise((resolve) => setTimeout(resolve, 300)); // delay 300ms

        //CAPACITOR BROWSER its closed in authCallback.tsx
      } else if (url.includes("stripe-callback")) {
        console.log("💳 Stripe payment callback detected from native scheme");

        // Close the Capacitor Browser first
        await Browser.close();

        // Extract query params from URL
        const urlObj = new URL(url);
        const paymentStatus = urlObj.searchParams.get("payment");
        const productId = urlObj.searchParams.get("productId");

        // Dispatch custom event to notify PaymentMethodSwipeable
        const paymentEvent = new CustomEvent("stripePaymentComplete", {
          detail: {
            success: paymentStatus === "success",
            productId: productId,
          },
        });
        window.dispatchEvent(paymentEvent);
        console.log("🔔 Dispatched stripePaymentComplete event");

        // Navigate to home (PaymentMethodSwipeable will handle dialog/credits)
        navigate("/home", { replace: true });
      } else if (url.includes("free-plan-activated")) {
        console.log("🆓 FREE plan activated callback detected from native scheme");

        // Close the Capacitor Browser first
        await Browser.close();

        // Extract query params from URL
        const urlObj = new URL(url);
        const success = urlObj.searchParams.get("success") === "true";

        // Dispatch custom event to notify components
        const freePlanEvent = new CustomEvent("freePlanActivated", {
          detail: {
            success: success,
            message: "FREE plan activated successfully!",
          },
        });
        window.dispatchEvent(freePlanEvent);
        console.log("🔔 Dispatched freePlanActivated event");

        // Navigate to home (components will handle dialog)
        navigate("/home", { replace: true });
      }
    });

    // Cleanup function to remove listener
    return () => {
      listener.then(l => l.remove());
    };
  }, [callbackUri, handleRedirectCallback, user, navigate]);

  useEffect(() => {
    if (isIOS || isAndroid) {
      try {
        console.log("Attempting to set status bar color");
        if (StatusBar.setBackgroundColor) {
          StatusBar.setBackgroundColor({ color: "#000000" }).catch((err) => {
            console.log("Error in color of status bar:", err);
          });
        } else {
          console.log("StatusBar.setBackgroundColor its not available");
        }
      } catch (err) {
        console.log("Bug with status bar:", err);
      }
    }
  }, []);

  useEffect(() => {
    if (isAndroid && StatusBar.setOverlaysWebView) {
      StatusBar.setOverlaysWebView({ overlay: false }).catch(() => { });
      console.log("StatusBar.setOverlaysWebView set to false");
    } else {
      console.log("StatusBar.setOverlaysWebView its not available");
    }
  }, []);

  // // Initialize AdMob for native platforms
  // useEffect(() => {
  //   if (isPlatform("hybrid")) {
  //     initializeAdMob()
  //       .then((success) => {
  //         if (success) {
  //           console.log("[App] AdMob initialized successfully");
  //         }
  //       })
  //       .catch((error) => {
  //         console.error("[App] Failed to initialize AdMob:", error);
  //       });
  //   }
  // }, []);

  // New useEffect for app version check
  useEffect(() => {
    const checkAppVersion = async () => {
      // Only run on iOS and Android
      if (!isPlatform("hybrid")) {
        return;
      }

      try {
        //get the app info and the min versions
        console.log("[Update Check] Starting version check...");
        const appInfo = await getAppInfo();
        //get the min versions
        console.log("[Update Check] Current App Info:", appInfo.version + " " + appInfo.platform);

        const minVersions = await getMinVersions();

        console.log("[Update Check] Minimum Required Versions from Backend:", "IOS: " + minVersions.minIosVersion + " Android: " + minVersions.minAndroidVersion);

        // Explicitly check for the default error response from getMinVersions
        if (minVersions && minVersions.minIosVersion === "0.0.0" && minVersions.minAndroidVersion === "0.0.0") {
          // This condition indicates that the backend call likely failed silently.
          // We throw an error here to trigger our catch block and show a user-friendly message.
          throw new Error("Failed to fetch valid minimum versions from the server.");
        }

        //check if the app version is up to date
        const required = isUpdateRequired(appInfo.platform, appInfo.version, minVersions);
        console.log("[Update Check] Is update required?", required);

        //if the app version is not up to date, show the update dialog
        if (required) {
          setShowUpdateDialog(true);
        }
      } catch (error) {
        console.error("Error checking app version:", error);
        if (error instanceof MinVersionsFetchError && error.type === "network") {
          setStartupError("Internet connection is required to use the app.");
          return;
        }

        setStartupError("Oops! Something went wrong on our end. Please contact to us at https://www.rotogpt.com/contact - we will work to fix it!");
      }
    };

    //check the app version
    checkAppVersion();
  }, []);

  //function to redirect to the store
  const handleUpgrade = async () => {
    await redirectToStore();
  };

  useEffect(() => {
    if (isAuthenticated && user) {
      const method = user?.sub?.split("|")[0] || "unknown"; // e.g. google-oauth2, apple
      const authId = user?.sub || "unknown";
      trackAppLogin(authId, method, true);
      void setAnalyticsUser(authId, { user_tier: "registered_free" });
      consumeAnalyticsSignupIntent(); // clear leftover intent; sign_up uses Auth0 first-login claim only
      // Namespaced claim from Auth0 post-login Action (logins_count === 1). Not a callback URL.
      const isNewAuth0User = user["https://rotogpt.com/is_new_user"] === true;
      if (isNewAuth0User) {
        void trackSignUp(authId, method, getAnalyticsHadUsedFreeCredit(), getAnalyticsAnonAuthId());
      }
    }
  }, [isAuthenticated, user]);

  // Handle automatic redirections based on auth state
  useEffect(() => {
    if (isLoading) return; // Wait for Auth0 to finish loading

    const currentPath = window.location.pathname;

    if (isAuthenticated) {
      // Authenticated users: redirect from guest (/) or /get-started to /home
      // Also keep /home-free for legacy URLs that still hit the alias redirect
      if (currentPath === "/get-started" || currentPath === "/" || currentPath === "/home-free") {
        navigate("/home", { replace: true });
      }
    }
    // Note: No automatic redirection for non-authenticated users
    // /get-started must remain accessible for login
  }, [isLoading, isAuthenticated, navigate]);

  // Show update dialog if required (this blocks everything else)
  if (showUpdateDialog) {
    return (
      <UpdateDialog
        open={true}
        onClose={() => { }} // Do nothing on close to make it mandatory
        onSubmit={handleUpgrade}
      />
    );
  }

  // NEW: Render a specific error message if a critical startup fetch fails
  if (startupError) {
    return (
      <ThemeProvider>
        <CssBaseline />
        <Box
          sx={{
            bgcolor: "#000",
            color: "white",
            height: "100vh",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            padding: 2,
            textAlign: "center",
          }}
        >
          <Typography variant="h5">{startupError}</Typography>
        </Box>
      </ThemeProvider>
    );
  }

  if (isDev && !isDevAuthenticated) {
    return (
      <ThemeProvider>
        <CssBaseline />
        <LoginDialog
          open={openLoginDialog}
          onClose={() => setOpenLoginDialog(true)} // Not allow to close without valid credentials
          onSubmit={handleLoginSubmit}
        />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <CssBaseline />
      {/* if the user is authenticated, show the simple splash screen (not on Yahoo callback) */}
      {isAuthenticated && showSplash && !onYahooCallback && (
        <SimpleSplashScreen
          onVideoEnd={() => {
            setShowSplash(false);
            setVideoEnded(true);
          }}
        />
      )}

      {/* Show SuspenseLoader if loading and (video ended or not authenticated) */}
      {isLoading && (videoEnded || !isAuthenticated) && !showSplash && !onYahooCallback && <SuspenseLoader />}

      {/* Show content if video ended, not authenticated, or Yahoo callback (must not block AuthCallback) */}
      {(videoEnded || !isAuthenticated || onYahooCallback) && content}
    </ThemeProvider>
  );
}
export default App;
